import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ChalkboardTeacher, GraduationCap, ClipboardText, BookOpen, CalendarBlank,
  ClockCounterClockwise, Star, CurrencyDollar, TrendUp, Check, PaperPlaneTilt,
} from "@phosphor-icons/react";

function GradeDialog({ submission, onClose, onSaved }) {
  const { t } = useTranslation();
  const [grade, setGrade] = useState(submission?.grade || "");
  const [feedback, setFeedback] = useState(submission?.feedback || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/homework/submissions/${submission.id}/grade`, { grade, feedback });
      toast.success(t("teacherPortal.toasts.graded"));
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("teacherPortal.grade.title", { name: submission?.student_name || "" })}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-md bg-secondary/40 text-sm">
            <div className="font-semibold">{submission?.homework_title}</div>
            {submission?.note && <div className="text-xs text-muted-foreground mt-1">{submission.note}</div>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.grade.grade")}</Label>
            <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder={t("teacherPortal.grade.gradePh")} className="mt-1.5 h-10" data-testid="grade-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.grade.feedback")}</Label>
            <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} className="mt-1.5" data-testid="feedback-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="grade-save-btn">{saving ? t("common.saving") : t("teacherPortal.grade.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HomeworkDialog({ classes, teacherId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    class_id: "", title: "", description: "",
    assigned_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.class_id || !f.title) return toast.error(t("teacherPortal.homework.fillFields"));
    setSaving(true);
    try {
      const cls = classes.find((c) => c.id === f.class_id);
      await api.post("/homework", {
        ...f, teacher_id: teacherId, student_ids: cls?.student_ids || [],
      });
      toast.success(t("teacherPortal.toasts.homeworkCreated"));
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("teacherPortal.homework.newTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.homework.class")}</Label>
            <select
              value={f.class_id}
              onChange={(e) => setF({ ...f, class_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="hw-class-select"
            >
              <option value="">{t("teacherPortal.homework.selectClass")}</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.homework.titleField")}</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5 h-10" data-testid="hw-title-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">{t("common.description")}</Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.homework.assigned")}</Label>
              <Input type="date" value={f.assigned_date} onChange={(e) => setF({ ...f, assigned_date: e.target.value })} className="mt-1.5 h-10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">{t("teacherPortal.homework.due")}</Label>
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1.5 h-10" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="hw-save-btn">{saving ? t("common.creating") : t("teacherPortal.homework.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TeacherPortal() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(null);
  const [newHw, setNewHw] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/teacher/portal");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-muted-foreground p-6">{t("common.loadingDots")}</div>;
  if (!data) return <div className="text-sm text-destructive p-6">{t("common.noData")}</div>;

  const c = data.counters || {};
  const teacher = data.teacher || {};

  const kpis = [
    { label: t("teacherPortal.kpi.students"), value: c.students || 0, icon: GraduationCap, tone: "bg-primary/10 text-primary", tid: "kpi-my-students" },
    { label: t("teacherPortal.kpi.classes"), value: c.classes || 0, icon: BookOpen, tone: "bg-sky-500/10 text-sky-600 dark:text-sky-400", tid: "kpi-my-classes" },
    { label: t("teacherPortal.kpi.lessonsToday"), value: c.lessons_today || 0, icon: CalendarBlank, tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400", tid: "kpi-lessons-today" },
    { label: t("teacherPortal.kpi.ungraded"), value: c.ungraded_submissions || 0, icon: ClipboardText, tone: (c.ungraded_submissions || 0) > 0 ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", tid: "kpi-ungraded" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
            {t("teacherPortal.header")}
          </div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>
            {t("teacherPortal.hello", { name: teacher.full_name || user?.name || "" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("teacherPortal.subtitle")}</p>
        </div>
        <Button className="rounded-full gap-2" onClick={() => setNewHw(true)} data-testid="new-hw-btn">
          <BookOpen size={16} weight="bold" /> {t("teacherPortal.homework.newBtn")}
        </Button>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={k.tid} className="border-border/60"><CardContent className="p-4">
            <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", k.tone)}>
              <k.icon size={20} weight="duotone" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-3">{k.label}</div>
            <div className="text-3xl font-bold font-mono mt-0.5">{k.value}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Salary card */}
      <Card className="border-border/60 bg-gradient-to-br from-primary/5 to-primary/10"><CardContent className="p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("teacherPortal.earnings.header")}</div>
            <div className="text-4xl font-bold font-mono mt-1">${(c.monthly_estimated_earnings || 0).toLocaleString()}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t("teacherPortal.earnings.detail", {
                salary: `$${(c.monthly_salary || 0).toLocaleString()}`,
                lessons: c.lessons_this_month || 0,
              })}
            </div>
          </div>
          <TrendUp size={64} weight="duotone" className="text-primary opacity-30" />
        </div>
      </CardContent></Card>

      <Tabs defaultValue="schedule">
        <TabsList className="flex-wrap">
          <TabsTrigger value="schedule" data-testid="tp-tab-schedule"><CalendarBlank size={14} className="mr-1.5" /> {t("teacherPortal.tabs.schedule")}</TabsTrigger>
          <TabsTrigger value="classes" data-testid="tp-tab-classes"><BookOpen size={14} className="mr-1.5" /> {t("teacherPortal.tabs.classes")}</TabsTrigger>
          <TabsTrigger value="students" data-testid="tp-tab-students"><GraduationCap size={14} className="mr-1.5" /> {t("teacherPortal.tabs.students")}</TabsTrigger>
          <TabsTrigger value="homework" data-testid="tp-tab-homework"><ClipboardText size={14} className="mr-1.5" /> {t("teacherPortal.tabs.homework")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tp-tab-reviews"><Star size={14} className="mr-1.5" /> {t("teacherPortal.tabs.reviews")}</TabsTrigger>
        </TabsList>

        {/* Schedule */}
        <TabsContent value="schedule" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <ClockCounterClockwise size={16} weight="duotone" /> {t("teacherPortal.schedule.today")}
              </h3>
              {(data.lessons_today || []).length === 0 && <p className="text-sm text-muted-foreground">{t("teacherPortal.schedule.noneToday")}</p>}
              <ul className="space-y-2">
                {(data.lessons_today || []).map((l) => (
                  <li key={l.id} className="p-3 rounded-md border border-border/60 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{l.room || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{l.start_time} - {l.end_time}</div>
                      <Badge variant="outline" className="text-[10px] mt-1">{l.status}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent></Card>

            <Card className="border-border/60"><CardContent className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <CalendarBlank size={16} weight="duotone" /> {t("teacherPortal.schedule.thisWeek")}
              </h3>
              {(data.lessons_week || []).length === 0 && <p className="text-sm text-muted-foreground">{t("teacherPortal.schedule.noneWeek")}</p>}
              <ul className="space-y-1.5">
                {(data.lessons_week || []).map((l) => (
                  <li key={l.id} className="p-2 rounded-md hover:bg-secondary/40 text-sm flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{l.title}</div>
                      <div className="text-xs text-muted-foreground font-mono">{l.date} · {l.start_time}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{l.room || "—"}</Badge>
                  </li>
                ))}
              </ul>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Classes */}
        <TabsContent value="classes" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data.classes || []).map((cls) => (
              <Card key={cls.id} className="border-border/60 card-hover"><CardContent className="p-5">
                <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{cls.name}</h3>
                <div className="text-xs text-muted-foreground mt-1">{cls.schedule || "—"}</div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="secondary">{(cls.student_ids || []).length} {t("classes.students")}</Badge>
                  {cls.room && <Badge variant="outline">{cls.room}</Badge>}
                </div>
                <Button asChild variant="outline" size="sm" className="w-full mt-3">
                  <Link to={`/classes`} data-testid={`goto-class-${cls.id}`}>{t("teacherPortal.classes.open")}</Link>
                </Button>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        {/* Students */}
        <TabsContent value="students" className="mt-4">
          <Card className="border-border/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tp-students-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    {[t("students.table.student"), t("students.table.id"), t("students.table.level"), t("students.table.contact")].map((h, i) =>
                      <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data.students || []).map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                      <td className="px-4 py-3">
                        <Link to={`/students/${s.id}`} className="font-semibold hover:text-primary transition-colors">{s.full_name}</Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{s.student_number}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="font-mono text-[10px]">{s.english_level || "—"}</Badge></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{s.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Homework */}
        <TabsContent value="homework" className="mt-4 space-y-4">
          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-widest text-muted-foreground">{t("teacherPortal.homework.assigned")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data.homework || []).length === 0 && <p className="text-sm text-muted-foreground">{t("teacherPortal.homework.empty")}</p>}
              {(data.homework || []).map((h) => (
                <Card key={h.id} className="border-border/60"><CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold">{h.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t("portal.homework.due", { date: h.due_date })}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {(h.completed_by || []).length}/{(h.student_ids || []).length}
                    </Badge>
                  </div>
                </CardContent></Card>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-widest text-muted-foreground">{t("teacherPortal.submissions.header")}</h3>
            <Card className="border-border/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 border-b border-border">
                    <tr className="text-left">
                      {[t("teacherPortal.submissions.student"), t("teacherPortal.submissions.homework"),
                        t("teacherPortal.submissions.submitted"), t("teacherPortal.submissions.grade"), ""].map((h, i) =>
                        <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.pending_submissions || []).length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("teacherPortal.submissions.empty")}</td></tr>
                    )}
                    {(data.pending_submissions || []).map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-semibold">{s.student_name}</td>
                        <td className="px-4 py-3 text-xs">{s.homework_title}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{new Date(s.submitted_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          {s.grade ? (
                            <Badge className="bg-emerald-500 text-white gap-1"><Check size={10} weight="bold" /> {s.grade}</Badge>
                          ) : (
                            <Badge variant="outline">{t("teacherPortal.submissions.notGraded")}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" onClick={() => setGrading(s)} data-testid={`grade-btn-${s.id}`}>
                            {t("teacherPortal.submissions.gradeBtn")}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Reviews */}
        <TabsContent value="reviews" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(data.reviews || []).length === 0 && <p className="text-sm text-muted-foreground">{t("teacherPortal.reviews.empty")}</p>}
            {(data.reviews || []).map((r) => (
              <Card key={r.id} className="border-border/60"><CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{r.period}</div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((n) => <Star key={n} size={14} weight={n <= r.rating ? "fill" : "regular"} className={n <= r.rating ? "text-amber-500" : "text-muted-foreground"} />)}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">{r.comments}</div>
                <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">{t("teacherPortal.reviews.by", { name: r.reviewer || "—" })}</div>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {grading && <GradeDialog submission={grading} onClose={() => setGrading(null)} onSaved={load} />}
      {newHw && <HomeworkDialog classes={data.classes || []} teacherId={teacher.id} onClose={() => setNewHw(false)} onSaved={load} />}
    </div>
  );
}
